import {
  faEllipsisVertical,
  faPencil,
  faTrash,
} from "@fortawesome/pro-solid-svg-icons";
import {
  Badge,
  Button,
  Dropdown,
  Grid,
  Icon,
  Input,
  Message,
  Table,
  TextArea,
} from "@intility/bifrost-react";
import Drawer from "@intility/bifrost-react/Drawer";
import Modal from "@intility/bifrost-react/Modal";
import { useState } from "react";
import {
  useApproveSpiffeCluster,
  useDeleteSpiffeCluster,
  useRejectSpiffeCluster,
  useSpiffeClusters,
  useUpdateSpiffeCluster,
} from "../../api/spiffeClusters";
import type {
  SpiffeCluster,
  UpdateSpiffeClusterRequest,
} from "../../api/types";
import { useCapabilities } from "../../auth/capabilities";
import { PageLayout } from "../../components/layout/PageLayout";
import { formatDate } from "../../lib/clientTypes";

const STATUS_BADGE: Record<
  string,
  "neutral" | "default" | "success" | "warning" | "alert"
> = {
  pending: "neutral",
  approved: "default",
  active: "success",
};

const TOKEN_EXPIRY_WARNING_DAYS = 14;

/// An expired TokenReview token does not break running agents — it breaks the next agent pod
/// restart, which can be weeks later. Surfacing the date here is what lets an operator connect
/// the two events.
function TokenExpiry({ expiresAt }: { expiresAt: string | null }) {
  if (!expiresAt) return <span>—</span>;

  const expiry = new Date(expiresAt);
  if (Number.isNaN(expiry.getTime())) return <span>—</span>;

  const days = Math.floor((expiry.getTime() - Date.now()) / 86_400_000);

  if (days < 0) {
    return <Badge state="alert">Expired {formatDate(expiresAt)}</Badge>;
  }
  if (days <= TOKEN_EXPIRY_WARNING_DAYS) {
    return (
      <Badge state="warning">
        {formatDate(expiresAt)} ({days}d)
      </Badge>
    );
  }
  return (
    <span>
      {formatDate(expiresAt)} ({days}d)
    </span>
  );
}

export default function SpiffeClusters() {
  const { can } = useCapabilities();
  // Module-scoped: approve/reject and decommission are the trust-domain decisions.
  // A consumer team owns its own cluster and can rotate that cluster's token, but does
  // so through the CLI — the Portal only exposes the module-owner view.
  const canManage = can("spiffe.manage");

  const { data: clusters, isLoading, error } = useSpiffeClusters();
  const approve = useApproveSpiffeCluster();
  const reject = useRejectSpiffeCluster();
  const updateCluster = useUpdateSpiffeCluster();
  const deleteCluster = useDeleteSpiffeCluster();

  const pendingClusters = clusters?.filter((c) => c.status === "pending") ?? [];
  const otherClusters = clusters?.filter((c) => c.status !== "pending") ?? [];

  const [editCluster, setEditCluster] = useState<SpiffeCluster | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<SpiffeCluster | null>(null);

  if (isLoading) {
    return (
      <div className="bf-content bfl-page-padding">
        <Grid
          gap={16}
          style={{
            minHeight: "200px",
            placeContent: "center",
            textAlign: "center",
          }}
        >
          <Icon.Spinner size={32} />
          <p className="bf-p">Loading clusters...</p>
        </Grid>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bf-content bfl-page-padding">
        <Message state="warning" header="Failed to load clusters">
          {error instanceof Error
            ? error.message
            : "An unexpected error occurred."}
        </Message>
      </div>
    );
  }

  return (
    <PageLayout
      breadcrumbs={[{ label: "Home", to: "/" }, { label: "SPIFFE Clusters" }]}
      title="SPIFFE clusters"
      description="Consumer clusters registered for SPIFFE workload attestation. Approve pending clusters to enable automatic infrastructure provisioning."
    >
      <Grid gap={24}>
        {(approve.error ||
          reject.error ||
          updateCluster.error ||
          deleteCluster.error) && (
          <Message state="warning" header="Action failed">
            {((approve.error ??
            reject.error ??
            updateCluster.error ??
            deleteCluster.error instanceof Error)
              ? (approve.error ??
                reject.error ??
                updateCluster.error ??
                deleteCluster.error)
              : null
            )?.toString() ?? "An unexpected error occurred."}
          </Message>
        )}

        {pendingClusters.length > 0 && (
          <>
            <h3>Pending approval</h3>
            <Table>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Cluster</Table.HeaderCell>
                  <Table.HeaderCell>API URL</Table.HeaderCell>
                  <Table.HeaderCell>Requested by</Table.HeaderCell>
                  <Table.HeaderCell>Requested</Table.HeaderCell>
                  {canManage && <Table.HeaderCell> </Table.HeaderCell>}
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {pendingClusters.map((c) => (
                  <Table.Row key={c.id}>
                    <Table.Cell>
                      <code
                        style={{ fontFamily: "monospace", fontSize: "0.875em" }}
                      >
                        {c.cluster}
                      </code>
                    </Table.Cell>
                    <Table.Cell>{c.apiUrl}</Table.Cell>
                    <Table.Cell>{c.requestedBy}</Table.Cell>
                    <Table.Cell>{formatDate(c.createdAt)}</Table.Cell>
                    <Table.Cell>
                      {canManage && (
                        <div
                          style={{
                            display: "flex",
                            gap: "8px",
                            justifyContent: "flex-end",
                          }}
                        >
                          <Button
                            small
                            disabled={approve.isPending || reject.isPending}
                            onClick={() => approve.mutate(c.cluster)}
                          >
                            Approve
                          </Button>
                          <ClusterKebab
                            cluster={c}
                            onEdit={setEditCluster}
                            onDelete={setDeleteTarget}
                          />
                        </div>
                      )}
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </>
        )}

        {otherClusters.length > 0 && (
          <>
            <h3>Registered clusters</h3>
            <Table>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Cluster</Table.HeaderCell>
                  <Table.HeaderCell>API URL</Table.HeaderCell>
                  <Table.HeaderCell>Status</Table.HeaderCell>
                  <Table.HeaderCell>Approved by</Table.HeaderCell>
                  <Table.HeaderCell>Token expires</Table.HeaderCell>
                  <Table.HeaderCell>Created</Table.HeaderCell>
                  {canManage && <Table.HeaderCell> </Table.HeaderCell>}
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {otherClusters.map((c) => (
                  <Table.Row key={c.id}>
                    <Table.Cell>
                      <code
                        style={{ fontFamily: "monospace", fontSize: "0.875em" }}
                      >
                        {c.cluster}
                      </code>
                    </Table.Cell>
                    <Table.Cell>{c.apiUrl}</Table.Cell>
                    <Table.Cell>
                      <Badge state={STATUS_BADGE[c.status] ?? "neutral"}>
                        {c.status}
                      </Badge>
                    </Table.Cell>
                    <Table.Cell>{c.approvedBy ?? "—"}</Table.Cell>
                    <Table.Cell>
                      <TokenExpiry expiresAt={c.tokenExpiresAt} />
                    </Table.Cell>
                    <Table.Cell>{formatDate(c.createdAt)}</Table.Cell>
                    {canManage && (
                      <Table.Cell>
                        <ClusterKebab
                          cluster={c}
                          onEdit={setEditCluster}
                          onDelete={setDeleteTarget}
                        />
                      </Table.Cell>
                    )}
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </>
        )}

        {!clusters || clusters.length === 0 ? (
          <Message state="neutral">No SPIFFE clusters registered.</Message>
        ) : null}
      </Grid>

      <EditDrawer
        cluster={editCluster}
        isOpen={editCluster !== null}
        onClose={() => setEditCluster(null)}
      />

      <DeleteModal
        cluster={deleteTarget}
        onClose={() => {
          if (deleteCluster.isPending) return;
          setDeleteTarget(null);
          deleteCluster.reset();
        }}
        onConfirm={() => {
          if (deleteTarget) deleteCluster.mutate(deleteTarget.cluster);
        }}
        isPending={deleteCluster.isPending}
        error={deleteCluster.error}
      />
    </PageLayout>
  );
}

function ClusterKebab({
  cluster,
  onEdit,
  onDelete,
}: {
  cluster: SpiffeCluster;
  onEdit: (c: SpiffeCluster) => void;
  onDelete: (c: SpiffeCluster) => void;
}) {
  return (
    <Dropdown
      content={
        <>
          <button type="button" onClick={() => onEdit(cluster)}>
            <Dropdown.Item icon={faPencil}>Edit</Dropdown.Item>
          </button>
          <button type="button" onClick={() => onDelete(cluster)}>
            <Dropdown.Item state="alert" icon={faTrash}>
              Delete
            </Dropdown.Item>
          </button>
        </>
      }
    >
      <Button
        variant="flat"
        noPadding
        aria-label={`Actions for ${cluster.cluster}`}
      >
        <Icon icon={faEllipsisVertical} />
      </Button>
    </Dropdown>
  );
}

function EditDrawer({
  cluster,
  isOpen,
  onClose,
}: {
  cluster: SpiffeCluster | null;
  isOpen: boolean;
  onClose: () => void;
}) {
  if (!cluster) return null;

  return (
    <Drawer
      header={`Edit ${cluster.cluster}`}
      isOpen={isOpen}
      onRequestClose={onClose}
    >
      {isOpen && <EditForm cluster={cluster} onClose={onClose} />}
    </Drawer>
  );
}

function EditForm({
  cluster,
  onClose,
}: {
  cluster: SpiffeCluster;
  onClose: () => void;
}) {
  const updateCluster = useUpdateSpiffeCluster();

  const [apiUrl, setApiUrl] = useState(cluster.apiUrl);
  const [token, setToken] = useState("");
  const [caCert, setCaCert] = useState("");
  const [error, setError] = useState<string | null>(null);

  const apiUrlDirty = apiUrl !== cluster.apiUrl;
  const tokenDirty = token !== "";
  const caCertDirty = caCert !== "";
  const dirty = apiUrlDirty || tokenDirty || caCertDirty;
  const saving = updateCluster.isPending;

  async function save() {
    if (!apiUrl.trim()) return;
    setError(null);

    const body: UpdateSpiffeClusterRequest = {};
    if (apiUrlDirty) body.apiUrl = apiUrl.trim();
    if (tokenDirty) body.token = token;
    if (caCertDirty) body.caCert = caCert || undefined;

    try {
      await updateCluster.mutateAsync({ cluster: cluster.cluster, body });
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to save changes.");
    }
  }

  return (
    <Grid gap={20} style={{ padding: "8px 20px 24px" }}>
      <Input
        label="API URL"
        value={apiUrl}
        onChange={(e) => setApiUrl(e.target.value)}
        required
      />
      <Input
        label="Token"
        value={token}
        onChange={(e) => setToken(e.target.value)}
        placeholder="Leave blank to keep current"
        type="password"
      />
      <TextArea
        label="CA certificate"
        optional
        value={caCert}
        onChange={(e) => setCaCert(e.target.value)}
        placeholder="Leave blank to keep current"
        rows={5}
      />

      {error && <Message state="alert">{error}</Message>}

      <div style={{ display: "flex", gap: 8 }}>
        <Button
          onClick={saving ? undefined : save}
          state={!dirty || saving || !apiUrl.trim() ? "inactive" : undefined}
        >
          {saving ? "Saving…" : "Save changes"}
        </Button>
        <Button
          variant="flat"
          onClick={saving ? undefined : onClose}
          state={saving ? "inactive" : undefined}
        >
          Cancel
        </Button>
      </div>
    </Grid>
  );
}

function DeleteModal({
  cluster,
  onClose,
  onConfirm,
  isPending,
  error,
}: {
  cluster: SpiffeCluster | null;
  onClose: () => void;
  onConfirm: () => void;
  isPending: boolean;
  error: Error | null;
}) {
  if (!cluster) return null;

  return (
    <Modal isOpen onRequestClose={onClose} header="Delete cluster" width={480}>
      <Grid gap={16}>
        <p className="bf-p" style={{ margin: 0 }}>
          This will{" "}
          {cluster.status === "pending" ? "permanently delete" : "decommission"}{" "}
          <strong>{cluster.cluster}</strong>.
          {cluster.status !== "pending" &&
            " The cluster will be marked for deletion and cleaned up by the reconciler."}{" "}
          This cannot be undone.
        </p>
        {error && (
          <Message state="alert" header="Delete failed">
            {error instanceof Error ? error.message : "Something went wrong."}
          </Message>
        )}
        <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
          <Button
            variant="flat"
            onClick={isPending ? undefined : onClose}
            state={isPending ? "inactive" : undefined}
          >
            Cancel
          </Button>
          <Button
            variant="filled"
            state={isPending ? "inactive" : "alert"}
            onClick={isPending ? undefined : onConfirm}
          >
            {isPending ? "Deleting…" : "Delete cluster"}
          </Button>
        </div>
      </Grid>
    </Modal>
  );
}
